/**
 * BranchSwitcher.tsx
 *
 * Dropdown in the top nav bar that allows multi-clinic users to switch
 * between branches without re-logging in.
 *
 * Only renders when the user has access to more than one clinic.
 * Single-clinic users see nothing — zero UI impact.
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Building2, ChevronDown, CheckCircle2 } from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';
import { toast } from 'react-hot-toast';

/**
 *  compact forces the icon-only trigger regardless of viewport. The sidebar rail is narrow at
 *        every breakpoint, so it cannot rely on the md-and-up text button.
 */
export function BranchSwitcher({ compact = false }: { compact?: boolean } = {}) {
  const { activeClinic, accessibleClinics, isMultiClinic, loadingClinics, switchClinic } = useTenant();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [switching, setSwitching] = useState(false);

  // Don't render for single-clinic users
  if (!isMultiClinic && !loadingClinics) return null;

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSwitch = async (clinicId: string) => {
    if (clinicId === activeClinic?.clinicId) {
      handleClose();
      return;
    }
    setSwitching(true);
    handleClose();
    const ok = await switchClinic(clinicId);
    setSwitching(false);
    if (ok) {
      const name = accessibleClinics.find(c => c.clinicId === clinicId)?.clinicName ?? clinicId;
      toast.success(`Switched to ${name}`);
      // Reload page so all data refreshes for the new clinic context
      window.location.reload();
    } else {
      toast.error('Access denied for that branch');
    }
  };

  return (
    <>
      <Tooltip title="Switch branch">
        <Button
          size="small"
          onClick={handleOpen}
          startIcon={switching ? <CircularProgress size={13} color="inherit" /> : <Building2 size={13} />}
          endIcon={<ChevronDown size={13} />}
          sx={{
            color: 'rgba(255,255,255,0.9)',
            textTransform: 'none',
            fontSize: '0.75rem',
            px: 1,
            py: 0.4,
            borderRadius: 1.5,
            border: '1px solid rgba(255,255,255,0.35)',
            bgcolor: 'rgba(255,255,255,0.13)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
            maxWidth: 180,
            display: compact ? 'none' : { xs: 'none', md: 'flex' },
          }}
          disabled={switching}
        >
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {loadingClinics ? 'Loading…' : (activeClinic?.clinicName ?? 'Select branch')}
          </Box>
        </Button>
      </Tooltip>

      {/* Mobile: icon-only button */}
      <Tooltip title={activeClinic?.clinicName ?? 'Switch branch'}>
        <Button
          size="small"
          onClick={handleOpen}
          sx={{
            minWidth: 0,
            p: 0.75,
            color: 'rgba(255,255,255,0.9)',
            border: '1px solid rgba(255,255,255,0.25)',
            bgcolor: 'rgba(255,255,255,0.08)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
            borderRadius: 1.5,
            display: compact ? 'flex' : { xs: 'flex', md: 'none' },
          }}
          disabled={switching}
        >
          <Building2 size={15} />
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{ sx: { mt: 1, minWidth: 260, maxHeight: 400 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
            Your Branches
          </Typography>
        </Box>
        <Divider />
        {accessibleClinics.map(clinic => {
          const isActive = clinic.clinicId === activeClinic?.clinicId;
          return (
            <MenuItem
              key={clinic.clinicId}
              onClick={() => handleSwitch(clinic.clinicId)}
              selected={isActive}
              sx={{ py: 1.25, px: 2, gap: 1 }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
                <Box
                  sx={{
                    width: 32, height: 32, borderRadius: 1,
                    bgcolor: isActive ? 'primary.main' : 'action.selected',
                    display: 'grid', placeItems: 'center', flexShrink: 0,
                  }}
                >
                  <Building2 size={15} color={isActive ? 'white' : undefined} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={isActive ? 700 : 400} noWrap>
                    {clinic.clinicName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {[clinic.city, clinic.state].filter(Boolean).join(', ') || clinic.branchCode || 'Branch'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {clinic.isPrimary && (
                    <Chip label="Primary" size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                  )}
                  {isActive && <CheckCircle2 size={15} color="#22c55e" />}
                </Stack>
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
